import React, { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  getPriceLists, upsertPriceList, deletePriceList, getPackagingOptions,
  getProductPackaging, packagingMapFromList, allowedPackagings, PRICE_LEVELS, fmt,
} from "@/lib/api";
import { Plus, Trash2, Pencil, Tags } from "lucide-react";
import { useT } from "@/i18n/I18nContext";
import { useProductTypes } from "@/context/ProductTypesContext";

const empty = {
  id: null, product_type: "Telemea", unit: "kg", currency: "RON",
  price_level_1: "", price_level_2: "", price_level_3: "", price_level_4: "", price_level_5: "",
};

export default function PriceLists() {
  const { t, td } = useT();
  const [rows, setRows] = useState([]);
  const [packagings, setPackagings] = useState([]);
  const [pkgMap, setPkgMap] = useState({});
  const [dialog, setDialog] = useState(null);

  const load = async () => {
    const [r, pk, pp] = await Promise.all([getPriceLists(), getPackagingOptions(), getProductPackaging()]);
    setRows(r); setPackagings(pk.filter((x) => x.active !== false)); setPkgMap(packagingMapFromList(pp));
  };
  useEffect(() => { (async () => { await load(); })(); }, []);

  const save = async (f) => {
    try {
      await upsertPriceList({
        product_type: f.product_type, unit: f.unit, currency: f.currency || "RON",
        price_level_1: Number(f.price_level_1) || 0, price_level_2: Number(f.price_level_2) || 0,
        price_level_3: Number(f.price_level_3) || 0, price_level_4: Number(f.price_level_4) || 0,
        price_level_5: Number(f.price_level_5) || 0,
      });
      toast.success(t("prices.saved"));
      setDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
  };

  const remove = async (id) => {
    try { await deletePriceList(id); toast.success(t("prices.entryRemoved")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
  };

  return (
    <Layout actions={
      <Button size="sm" onClick={() => setDialog({ ...empty })} data-testid="add-price-button">
        <Plus className="mr-1.5 h-4 w-4" /> {t("prices.addEdit")}
      </Button>
    }>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Tags className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("prices.title")} ({rows.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center">
              <Tags className="h-10 w-10 text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">{t("prices.empty")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto thin-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("common.product")}</TableHead>
                    <TableHead>{t("common.unit")}</TableHead>
                    {PRICE_LEVELS.map((l) => <TableHead key={l} className="text-right">{t("prices.level", { n: l })}</TableHead>)}
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id} data-testid={`price-row-${r.id}`}>
                      <TableCell className="font-medium">{td(r.product_type)}</TableCell>
                      <TableCell><Badge variant="outline" className="font-mono text-[10px]">{r.unit}</Badge></TableCell>
                      {PRICE_LEVELS.map((l) => (
                        <TableCell key={l} className="text-right font-mono tabular-nums">{fmt(r[`price_level_${l}`], 2)}</TableCell>
                      ))}
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => setDialog({ ...empty, ...r })} data-testid={`price-edit-${r.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(r.id)} data-testid={`price-delete-${r.id}`}>
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <PriceDialog key={dialog ? (dialog.id ?? "new") : "closed"} dialog={dialog} packagings={packagings} pkgMap={pkgMap} onClose={() => setDialog(null)} onSave={save} />
    </Layout>
  );
}

function PriceDialog({ dialog, packagings, pkgMap, onClose, onSave }) {
  const { t, td } = useT();
  const { productTypes } = useProductTypes();
  const [f, setF] = useState(dialog);
  if (!f) return null;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  // Restrict packaging to the product's fixed list (kg always allowed; keep current unit while editing).
  const opts = allowedPackagings(pkgMap, f.product_type, packagings);
  const onProduct = (v) => {
    const allowed = allowedPackagings(pkgMap, v, packagings).map((u) => u.label);
    setF((x) => ({ ...x, product_type: v, unit: allowed.includes(x.unit) ? x.unit : (allowed[0] || "kg") }));
  };
  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{f.id ? t("prices.editEntry") : t("prices.addEntry")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5 col-span-1">
              <Label>{t("common.product")}</Label>
              <Select value={f.product_type} onValueChange={onProduct} disabled={!!f.id}>
                <SelectTrigger data-testid="price-product-select"><SelectValue /></SelectTrigger>
                <SelectContent>{productTypes.map((p) => <SelectItem key={p} value={p}>{td(p)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.packaging")}</Label>
              <Select value={f.unit} onValueChange={(v) => set("unit", v)} disabled={!!f.id}>
                <SelectTrigger data-testid="price-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent>{opts.map((u) => <SelectItem key={u.label} value={u.label}>{u.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.currency")}</Label>
              <Input value={f.currency} onChange={(e) => set("currency", e.target.value)} data-testid="price-currency-input" />
            </div>
          </div>
          <div className="grid grid-cols-5 gap-2">
            {PRICE_LEVELS.map((l) => (
              <div key={l} className="space-y-1.5">
                <Label className="text-xs">{t("prices.level", { n: l })}</Label>
                <Input type="number" className="text-right font-mono" value={f[`price_level_${l}`]} onChange={(e) => set(`price_level_${l}`, e.target.value)} data-testid={`price-level-${l}-input`} />
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={() => onSave(f)} data-testid="price-save-button">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
